/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { useContextProps } from 'react-aria-components/slots';
import { IconContext } from './context';
import styles from './styles.module.css';
import type { IconProps } from './types';

/**
 * Icon - A wrapper component for displaying SVG icons with consistent sizing
 *
 * Provides a standardized container for SVG icons with built-in size variants and
 * proper accessibility support. Works seamlessly with the Accelint icon library
 * and supports custom SVG elements with consistent styling and alignment.
 *
 * @param props - {@link IconProps}
 * @param props.ref - Ref to the span element.
 * @param props.children - SVG icon element to display.
 * @param props.className - Optional CSS class name.
 * @param props.size - Size variant for the icon.
 * @returns The rendered Icon component.
 *
 * @example
 * ```tsx
 * // Basic icon usage
 * <Icon>
 *   <Settings />
 * </Icon>
 * ```
 *
 * @example
 * ```tsx
 * // Icon with different sizes
 * <Icon size="small">
 *   <User />
 * </Icon>
 * <Icon size="large">
 *   <Dashboard />
 * </Icon>
 * ```
 *
 * @example
 * ```tsx
 * // Icon in button context (automatically inherits sizing)
 * <Button>
 *   <Icon><Plus /></Icon>
 *   Add Item
 * </Button>
 * ```
 */
export function Icon({ ref, ...props }: IconProps) {
  [props, ref] = useContextProps(props, ref ?? null, IconContext);

  const { children, className, size = 'medium', ...rest } = props;

  return (
    <span
      {...rest}
      ref={ref}
      className={clsx(styles.icon, className)}
      data-size={size}
    >
      {children}
    </span>
  );
}
